import { setAuthData, setIsLoggedIn } from 'redux/store/slices/authSlice'
import { setLeadId, setUserData } from 'redux/store/slices/userSlice'
import TokenService from 'services/token.service'
import { CreateLeadPayload, CreateLeadResponse } from 'share/types/api/credits'
import type {
  GetCitiesPayload,
  GetCitiesResponse,
  RecordTypeAppPayload,
  ResendLeadOtpPayload,
  VerifyLeadOtpPayload,
  VerifyLeadOtpResponse,
  VerifyOTPPayload
} from 'share/types/api/leads'
import { GetUserResponse } from 'share/types/api/leads'

import { api } from '.'

const leadApi = api.injectEndpoints({
  endpoints: build => ({
    getCities: build.query<GetCitiesResponse, GetCitiesPayload>({
      query: () => ({
        url: '/directories/cities/',
        method: 'get'
      }),
      transformResponse: (res: GetCitiesResponse, _, args) => {
        if (args && args.filterByHasDelivery) {
          return res.filter(city => city.has_delivery)
        }
        return res
      }
    }),
    getUser: build.query<GetUserResponse, void>({
      query: () => ({
        url: '/users/me/',
        method: 'get'
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setUserData(data))
        } catch (error) {
          console.log(error)
        }
      }
    }),
    createLead: build.mutation<CreateLeadResponse, CreateLeadPayload>({
      query: body => ({
        url: '/credits/leads/',
        method: 'post',
        body
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setLeadId(data.id))
        } catch (error) {
          console.log(error)
          dispatch(setLeadId(null))
        }
      }
    }),
    resendLeadOtp: build.mutation<void, ResendLeadOtpPayload>({
      query: uuid => ({
        url: `/credits/leads/${uuid}/resend-sms/`,
        method: 'post'
      })
    }),
    verifyLeadOtp: build.mutation<VerifyLeadOtpResponse, VerifyLeadOtpPayload>({
      query: ({ uuid, passcode, utm_params }) => ({
        url: `/credits/leads/${uuid}/verify-sms/`,
        method: 'post',
        body: {
          passcode,
          ...(utm_params && {
            utm_params: utm_params
          })
        }
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setIsLoggedIn(true))
          dispatch(setAuthData(data))
          TokenService.setJWTTokens({
            access: data.access,
            refresh: data.refresh
          })
        } catch (error) {
          dispatch(setIsLoggedIn(false))
        }
      }
    }),
    resendOtp: build.mutation<void, string>({
      query: step_uuid => ({
        url: `/callbacks/${step_uuid}/resend-otp/`,
        method: 'post'
      })
    }),
    verifyOtp: build.mutation<void, VerifyOTPPayload>({
      query: ({ step_uuid, passcode }) => ({
        url: `/callbacks/${step_uuid}/otp-verify/`,
        method: 'post',
        body: {
          passcode
        }
      })
    }),
    recordTypeApp: build.mutation<void, RecordTypeAppPayload>({
      query: ({ step_uuid, type }) => ({
        url: `/credits/${step_uuid}/create-from/`,
        method: 'post',
        body: { type }
      })
    })
  })
})

export const {
  useCreateLeadMutation,
  useGetCitiesQuery,
  useResendLeadOtpMutation,
  useVerifyLeadOtpMutation,
  useLazyGetUserQuery,
  useGetUserQuery,
  useResendOtpMutation,
  useRecordTypeAppMutation,
  useVerifyOtpMutation
} = leadApi
